import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { adminDiagnosticsApi, type AiqResultItem, type LevelResultItem } from '@/api/adminDiagnostics'
import { diagnosticResultQueryKey } from '@/hooks/admin/useDiagnosticLiveResult'
import { ModalShell } from './ModalShell'

interface Props {
  assignmentId: string
  studentName: string
  onClose: () => void
}

function accuracy(correct: number, total: number): number {
  return total > 0 ? Math.round((correct / total) * 100) : 0
}

function ResultChip({
  state,
  label,
}: {
  state: 'correct' | 'wrong' | 'unanswered'
  label: string
}) {
  const className = state === 'correct'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
    : state === 'wrong'
      ? 'border-rose-200 bg-rose-50 text-rose-700'
      : 'border-gray-200 bg-gray-50 text-gray-500'

  return (
    <span
      data-testid="diag-result-chip"
      className={`inline-flex min-w-[56px] items-center justify-center rounded-full border px-2.5 py-1 text-xs font-semibold ${className}`}
    >
      {label}
    </span>
  )
}

export function ResultModal({ assignmentId, studentName, onClose }: Props) {
  const { t } = useTranslation('admin')
  const resultQ = useQuery({
    queryKey: diagnosticResultQueryKey(assignmentId),
    queryFn: () => adminDiagnosticsApi.getResult(assignmentId),
    staleTime: 0,
  })
  const data = resultQ.data?.data.data
  const reportQ = useQuery({
    queryKey: ['diagnostic-report-summary', assignmentId],
    queryFn: () => adminDiagnosticsApi.getReport(assignmentId),
    enabled: data?.status === 'COMPLETED',
    retry: false,
  })
  const report = reportQ.data?.data.data

  return (
    <ModalShell title={t('diagnostic.result.title', { name: studentName })} onClose={onClose} panelClassName="max-w-3xl">
      {resultQ.isLoading ? (
        <div className="py-6 text-center text-gray-500">…</div>
      ) : resultQ.isError || !data ? (
        <p className="py-6 text-center text-sm text-red-600">{t('diagnostic.result.loadFailed')}</p>
      ) : (
        <div className="space-y-4">
          {data.status === 'COMPLETED' && (
            <section className="rounded-lg border border-primary-100 bg-primary-50 px-4 py-3">
              <p className="text-xs font-semibold text-primary-700">{t('diagnostic.result.recommendedCourseTitle')}</p>
              {reportQ.isLoading ? (
                <p className="mt-1 text-sm font-semibold text-primary-900">{t('diagnostic.result.loading')}</p>
              ) : report?.recommendedCourse ? (
                <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
                  <span data-testid="diag-result-recommended-course" className="font-semibold text-primary-950">
                    {report.recommendedCourse.course
                      ? t('diagnostic.result.recommendedCourse', { course: report.recommendedCourse.course })
                      : t('diagnostic.result.recommendedCourseNone')}
                  </span>
                  {report.recommendedCourse.needsAiq && (
                    <span className="text-xs text-amber-700">{t('diagnostic.result.recommendedCourseNeedsAiq')}</span>
                  )}
                </div>
              ) : (
                <p className="mt-1 text-sm text-primary-900">{t('diagnostic.result.recommendedCourseUnavailable')}</p>
              )}
            </section>
          )}
          <div className="grid gap-4 lg:grid-cols-2">
            <section className="rounded-lg border border-gray-200 bg-white">
              <div className="border-b border-gray-100 px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-950">{t('diagnostic.result.aiqSection')}</h3>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {t('diagnostic.result.summary', { correct: data.aiq.correct, total: data.aiq.total })}
                    </p>
                  </div>
                  <div className="rounded-md bg-gray-50 px-2.5 py-1.5 text-right">
                    <p className="text-[11px] text-gray-500">{t('diagnostic.result.accuracyLabel')}</p>
                    <p className="text-sm font-semibold text-gray-900">{accuracy(data.aiq.correct, data.aiq.total)}%</p>
                  </div>
                </div>
              </div>
              <ul className="max-h-64 divide-y divide-gray-100 overflow-y-auto">
              {data.aiq.items.map((it: AiqResultItem) => (
                <li key={it.seq} className="grid grid-cols-[minmax(72px,1fr)_auto] items-center gap-3 px-4 py-2.5 text-sm">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-800">{t('diagnostic.result.question', { n: it.seq + 1 })}</p>
                    <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-gray-500">
                      {it.elapsedSec != null && <span>{t('diagnostic.result.elapsed', { sec: it.elapsedSec })}</span>}
                      {it.partialCredit != null && <span className="text-amber-600">{t('diagnostic.result.partial', { value: it.partialCredit })}</span>}
                    </div>
                  </div>
                  <div className="justify-self-end">
                    {it.isCorrect == null ? (
                      <ResultChip state="unanswered" label={t('diagnostic.result.unanswered')} />
                    ) : it.isCorrect ? (
                      <ResultChip state="correct" label={t('diagnostic.result.correct')} />
                    ) : (
                      <ResultChip state="wrong" label={t('diagnostic.result.wrong')} />
                    )}
                  </div>
                </li>
              ))}
              </ul>
            </section>

            <section className="rounded-lg border border-gray-200 bg-white">
              <div className="border-b border-gray-100 px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-950">{t('diagnostic.result.levelSection')}</h3>
                    {data.level && (
                      <p className="mt-0.5 text-xs text-gray-500">
                        {t('diagnostic.result.summary', { correct: data.level.correct, total: data.level.total })}
                        {' · '}
                        {t('diagnostic.result.score', { score: data.level.score })}
                      </p>
                    )}
                  </div>
                  {data.level && (
                    <div className="rounded-md bg-gray-50 px-2.5 py-1.5 text-right">
                      <p className="text-[11px] text-gray-500">{t('diagnostic.result.accuracyLabel')}</p>
                      <p className="text-sm font-semibold text-gray-900">{accuracy(data.level.correct, data.level.total)}%</p>
                    </div>
                  )}
                </div>
              </div>
              {data.level == null ? (
                <div className="px-4 py-8 text-center text-sm text-gray-500">{t('diagnostic.result.notGraded')}</div>
              ) : (
                <ul className="max-h-64 divide-y divide-gray-100 overflow-y-auto">
                  {data.level.items.map((it: LevelResultItem) => (
                    <li key={it.seq} className="grid grid-cols-[minmax(72px,1fr)_auto] items-center gap-3 px-4 py-2.5 text-sm">
                      <span className="font-medium text-gray-800">{t('diagnostic.result.question', { n: it.seq + 1 })}</span>
                      {it.isCorrect ? (
                        <ResultChip state="correct" label="O" />
                      ) : (
                        <ResultChip state="wrong" label="X" />
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <div className="flex justify-end">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
              {t('diagnostic.result.close')}
            </button>
          </div>
        </div>
      )}
    </ModalShell>
  )
}
